// When the widget's checkout opens as a full page in steps instead of the
// 420px drawer, and when a phone shows one rider card at a time.
//
// The owner (25 Sept, "pay now, details later" decision 1, accepted in full):
//   "Full-page checkout switches on by itself for long forms, and each
//    service can override it", and "one card on screen at a time on mobile"
//   for the rider cards.
//
// Judged from the question DEFINITIONS only (which questions a line asks, how
// many guests it holds, how many statements it has): never from an answer,
// never from which conditional questions are showing, never from a step the
// guest is on. The checkout decides once, when it opens, so the layout can
// never change while the guest is filling it in.
//
// Pure: no React, no network, no clock. Pinned by
// e2e/checkout-layout-logic.spec.ts.

/** services.config.checkout_layout: what a manager chose for one service. */
export const CHECKOUT_LAYOUT_SETTINGS = ["auto", "drawer", "page"] as const;
export type CheckoutLayoutSetting = (typeof CHECKOUT_LAYOUT_SETTINGS)[number];

/** How the checkout opens. */
export type CheckoutLayout = "drawer" | "page";

/** More inputs than this in one cart and "auto" opens the full page. */
export const CHECKOUT_PAGE_INPUTS = 8;

/** A container this wide or narrower is a phone (px). */
export const CHECKOUT_PHONE_MAX_PX = 640;

/**
 * A viewport this short or shorter gets the phone's bars too (px): a phone
 * held sideways (844×390) is wider than a phone, but a summary column beside
 * the form cannot fit its button on so short a screen.
 */
export const CHECKOUT_SHORT_MAX_PX = 500;

/**
 * A service's setting from its config, "auto" for anything else (absent, an
 * older value, a typo). services.config is public to the widget, and this key
 * holds nothing private: it is one of three words.
 */
export function checkoutLayoutSetting(config: unknown): CheckoutLayoutSetting {
  if (!config || typeof config !== "object" || Array.isArray(config)) return "auto";
  const v = (config as Record<string, unknown>).checkout_layout;
  return v === "drawer" || v === "page" ? v : "auto";
}

/**
 * The per-person questions that are only a name or a way to reach the
 * person: the booking-question presets' keys (first_name, surname, name /
 * full_name, mobile, email; last_name and phone as they are also spelled),
 * or a question the operator marked as holding a part of the name
 * (`profile`). Keys and markers only, never a label: "Emergency contact
 * mobile" under another key is a question beyond the name and contact.
 */
export const NAME_CONTACT_KEYS: readonly string[] = [
  "first_name",
  "surname",
  "last_name",
  "name",
  "full_name",
  "mobile",
  "phone",
  "email",
];

export type LayoutQuestion = { key: string; profile?: string | null };

export function isNameOrContactQuestion(field: LayoutQuestion): boolean {
  return NAME_CONTACT_KEYS.includes(field.key) || (typeof field.profile === "string" && field.profile !== "");
}

/** One cart line, as the layout reads it. */
export type CheckoutLayoutLine = {
  /** The line's service setting (checkoutLayoutSetting); absent = "auto". */
  setting?: CheckoutLayoutSetting | null;
  /** The questions asked of EACH guest on this line (definitions). */
  perPerson?: readonly LayoutQuestion[];
  /** How many guests the line holds (each answers perPerson). */
  party?: number;
  /**
   * The visit the line belongs to (the server's visit_key: one service at
   * one start in this cart). The once-per-booking questions and the
   * statements are asked once per visit, however many lines it has.
   */
  visitKey?: string | null;
  /** How many questions the visit asks once for the whole booking. */
  groupQuestions?: number;
  /** How many statements the visit's guest ticks before paying. */
  statements?: number;
};

function count(n: number | undefined | null): number {
  return typeof n === "number" && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** Does this line ask the guest anything at all? */
export function lineAsksAnything(line: CheckoutLayoutLine): boolean {
  return (line.perPerson?.length ?? 0) > 0 || count(line.groupQuestions) > 0 || count(line.statements) > 0;
}

/**
 * Every box and tick the cart asks for: per-person questions times the
 * line's guests, plus each visit's once-per-booking questions and its
 * statements, counted once per visit (the largest of its lines, as one
 * visit shows one set). A line with no visit key is its own visit.
 */
export function checkoutInputCount(lines: readonly CheckoutLayoutLine[]): number {
  let total = 0;
  const perVisit = new Map<string, number>();
  lines.forEach((line, i) => {
    total += (line.perPerson?.length ?? 0) * Math.max(1, count(line.party));
    const key = line.visitKey ? `v:${line.visitKey}` : `line:${i}`;
    const once = count(line.groupQuestions) + count(line.statements);
    perVisit.set(key, Math.max(perVisit.get(key) ?? 0, once));
  });
  for (const n of perVisit.values()) total += n;
  return total;
}

export type CheckoutLayoutDecision = {
  layout: CheckoutLayout;
  /** On a phone, the rider cards are shown one at a time (both layouts). */
  oneRiderPerScreen: boolean;
  /** Why, for the page's data attribute and the spec: which rule decided. */
  reason: "setting_page" | "setting_drawer" | "no_per_person" | "beyond_name_contact" | "many_inputs" | "short";
  /** checkoutInputCount of the lines. */
  inputs: number;
};

/**
 * The layout of one checkout. Only a line that asks something has a say (a
 * spa treatment or a ticket asks nothing, and one in the cart must not undo
 * a service's choice):
 *   * any asking line set to "page" → page;
 *   * every asking line set to "drawer" → drawer;
 *   * otherwise automatic. A cart with no per-person question keeps today's
 *     drawer, however many once-per-booking questions or statements it has
 *     (the page is for the guests' cards). With per-person questions: page
 *     when one goes beyond the name and contact questions, or the cart asks
 *     more than CHECKOUT_PAGE_INPUTS inputs in all; else the drawer.
 * oneRiderPerScreen: a phone (container ≤ CHECKOUT_PHONE_MAX_PX) and any
 * per-person question at all, whatever the layout.
 */
export function checkoutLayout({
  lines,
  phone,
}: {
  lines: readonly CheckoutLayoutLine[];
  phone: boolean;
}): CheckoutLayoutDecision {
  const inputs = checkoutInputCount(lines);
  const perPerson = lines.some((l) => (l.perPerson?.length ?? 0) > 0);
  const oneRiderPerScreen = phone && perPerson;
  const setting = (l: CheckoutLayoutLine): CheckoutLayoutSetting => l.setting ?? "auto";
  const decide = (layout: CheckoutLayout, reason: CheckoutLayoutDecision["reason"]): CheckoutLayoutDecision => ({
    layout,
    oneRiderPerScreen,
    reason,
    inputs,
  });
  const asking = lines.filter(lineAsksAnything);
  if (asking.some((l) => setting(l) === "page")) return decide("page", "setting_page");
  if (asking.length > 0 && asking.every((l) => setting(l) === "drawer")) return decide("drawer", "setting_drawer");
  if (!perPerson) return decide("drawer", "no_per_person");
  if (lines.some((l) => (l.perPerson ?? []).some((f) => !isNameOrContactQuestion(f))))
    return decide("page", "beyond_name_contact");
  if (inputs > CHECKOUT_PAGE_INPUTS) return decide("page", "many_inputs");
  return decide("drawer", "short");
}

/** Is a container of this width a phone? (Unknown width: not a phone.) */
export function isPhoneWidth(width: number | null | undefined): boolean {
  return typeof width === "number" && Number.isFinite(width) && width > 0 && width <= CHECKOUT_PHONE_MAX_PX;
}

/**
 * Does the checkout use the phone's presentation (the one-line summary at the
 * top, Back and the primary button in a bar at the bottom, one card on
 * screen)? A phone's width, or a screen too short for the summary column
 * beside the form (a phone held sideways). Unknown sizes: not a phone.
 */
export function isPhoneViewport(width: number | null | undefined, height: number | null | undefined): boolean {
  if (isPhoneWidth(width)) return true;
  return typeof height === "number" && Number.isFinite(height) && height > 0 && height <= CHECKOUT_SHORT_MAX_PX;
}
